import React from "react";
import {
    Box,
    Typography,
    Tooltip,
    Stack,
    Chip,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    CircularProgress,
    Fade,
    IconButton,
} from "@mui/material";
import {
    PsychologyAlt as PsychologyAltIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTooltipProps } from "./JobCard";

/* ------------------------------------------------------------------
   SKILLS MODAL
   Fully self-contained dialog for editing required skills on a JD.
   Can be dropped into any parent — not tied to RecruiterLanding.

   Props:
     open            — boolean, controls dialog visibility
     onClose()       — called when user cancels / closes
     onSave()        — called when user clicks "Save Config"
     selectedJD      — JD object { id, job_title, … }
     skillData       — { skill: { keySkills, additionalSkills, … } }
     setSkillData    — state setter for skillData
     skillsLoading   — boolean, shows spinner while fetching details
     savingSkills    — boolean, shows spinner on save button
     saveSuccess     — boolean, shows green success state briefly
------------------------------------------------------------------ */
const SkillsModal = ({
    open,
    onClose,
    onSave,
    selectedJD,
    skillData,
    setSkillData,
    skillsLoading,
    savingSkills,
    saveSuccess,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const ttProps = useTooltipProps(theme);

    // Add these state hooks at the beginning of the component
    const [keySkillsInput, setKeySkillsInput] = React.useState("");
    const [additionalSkillsInput, setAdditionalSkillsInput] = React.useState("");

    /* Normalise keySkills to always be an array for the Autocomplete */
    const keySkillsValue = skillData
        ? Array.isArray(skillData?.skill?.keySkills)
            ? skillData.skill.keySkills
            : typeof skillData?.skill?.keySkills === "string"
            ? skillData.skill.keySkills
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
            : []
        : [];

    const handleKeySkillsChange = (_, value) => {
        setSkillData({
            ...skillData,
            skill: { ...skillData.skill, keySkills: value },
        });
    };

    const hasUncommittedSkills = keySkillsInput.trim() !== "" || additionalSkillsInput.trim() !== "";

    return (
        <Dialog
            open={open}
            onClose={() => { if (!savingSkills) onClose(); }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    boxShadow: isDark
                        ? "0 20px 40px rgba(0,0,0,0.6)"
                        : "0 20px 40px -10px rgba(0, 0, 0, 0.2)",
                    bgcolor: theme.palette.background.paper,
                },
            }}
        >
            {/* ── HEADER ── */}
            <Box
                sx={{
                    background: isDark
                        ? theme.palette.background.default
                        : `linear-gradient(135deg, ${theme.palette.grey[800]} 0%, ${theme.palette.grey[900]} 100%)`,
                    px: 2.5,
                    pt: 2.5,
                    pb: 1.8,
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* Decorative glow */}
                <Box
                    sx={{
                        position: "absolute",
                        top: -20,
                        right: -10,
                        width: 70,
                        height: 70,
                        background: `radial-gradient(circle, ${alpha(
                            theme.palette.warning.main,
                            0.3
                        )} 0%, transparent 70%)`,
                        borderRadius: "50%",
                    }}
                />

                <Tooltip title="Close" placement="left" {...ttProps}>
                    <IconButton
                        size="small"
                        onClick={() => { if (!savingSkills) onClose(); }}
                        disabled={savingSkills}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            color: alpha("#fff", 0.6),
                            "&:hover": { color: "#fff", bgcolor: alpha("#fff", 0.1) },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            bgcolor: alpha("#fff", 0.1),
                            border: `1px solid ${alpha("#fff", 0.1)}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backdropFilter: "blur(10px)",
                        }}
                    >
                        <PsychologyAltIcon
                            sx={{
                                fontSize: 20,
                                color:
                                    theme.palette.mode === "dark"
                                        ? theme.palette.warning.light
                                        : "#fbbf24",
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "1rem",
                                fontWeight: 800,
                                color: "#fff",
                                lineHeight: 1.2,
                                fontFamily: "inherit",
                            }}
                        >
                            Required Skills Setup
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.68rem",
                                color: alpha("#fff", 0.6),
                                mt: 0.3,
                                fontWeight: 500,
                                fontFamily: "inherit",
                            }}
                        >
                            {selectedJD?.job_title || "Job Role"}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* ── CONTENT ── */}
            <DialogContent
                sx={{
                    px: 2.5,
                    pt: 2.5,
                    pb: 2,
                    bgcolor: theme.palette.background.paper,
                }}
            >
                {skillsLoading ? (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                        <CircularProgress
                            size={24}
                            sx={{ color: theme.palette.warning.main }}
                        />
                    </Box>
                ) : skillData ? (
                    <Fade in timeout={350}>
                        <Box>
                            {/* Info banner */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    bgcolor: alpha(theme.palette.warning.main, 0.06),
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                    borderRadius: "10px",
                                    px: 1.8,
                                    py: 1.2,
                                    mb: 2.5,
                                }}
                            >
                                <AutoAwesomeIcon
                                    sx={{
                                        fontSize: 14,
                                        color: theme.palette.warning.main,
                                        mt: 0.1,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: "0.68rem",
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        fontFamily: "inherit",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    Type a core competency and press{" "}
                                    <strong>Enter</strong>. These skills will be used by the
                                    LLM engine during candidate evaluation.
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: "0.78rem",
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    mb: 1.2,
                                    fontFamily: "inherit",
                                }}
                            >
                                Mandatory Evaluation Skills
                            </Typography>

                            <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={keySkillsValue}
                                inputValue={keySkillsInput}
                                onInputChange={(event, newInputValue) => setKeySkillsInput(newInputValue)}
                                onChange={(event, newValue) => {
                                    let updatedSkills = newValue;
                                    handleKeySkillsChange(event, updatedSkills);
                                    setKeySkillsInput("");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="e.g. React, Node.js, SQL…"
                                        variant="outlined"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "10px",
                                                bgcolor: theme.palette.background.default,
                                                fontSize: "0.78rem",
                                                "& fieldset": {
                                                    borderColor: theme.palette.divider,
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: alpha(
                                                        theme.palette.warning.main,
                                                        0.5
                                                    ),
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: theme.palette.warning.main,
                                                    borderWidth: "2px",
                                                },
                                            },
                                        }}
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            key={index}
                                            label={option}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                                color: theme.palette.warning.dark,
                                                fontWeight: 700,
                                                fontSize: "0.65rem",
                                                fontFamily: "inherit",
                                                borderRadius: "6px",
                                                m: 0.3,
                                                border: `1px solid ${alpha(
                                                    theme.palette.warning.main,
                                                    0.25
                                                )}`,
                                            }}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                            />
                            {keySkillsInput.trim() !== "" && (
                                <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontWeight: 600, color: "error.main" }}>
                                    ⚠️ Please press "Enter" to add "{keySkillsInput.trim()}" before saving.
                                </Typography>
                            )}
                        </Box>
                    </Fade>
                ) : null}
            </DialogContent>

            {/* ── ACTIONS ── */}
            <DialogActions
                sx={{
                    px: 2.5,
                    py: 1.8,
                    bgcolor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={savingSkills}
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        fontFamily: "inherit",
                        textTransform: "none",
                        px: 1.5,
                    }}
                >
                    Cancel
                </Button>

                <Tooltip
                    title={
                        hasUncommittedSkills ? "Please add your pending typed skill into the field by pressing Enter before saving." : saveSuccess ? "Configurations updated!" : "Lock changes into matching engine pipeline"
                    }
                    placement="top"
                    {...ttProps}
                >
                    <span>
                        <Button
                            variant="contained"
                            onClick={onSave}
                            disabled={savingSkills || skillsLoading || saveSuccess || hasUncommittedSkills}
                            sx={{
                                borderRadius: "8px",
                                fontSize: "0.72rem",
                                textTransform: "none",
                                fontFamily: "inherit",
                                fontWeight: 700,
                                minWidth: 110,
                                px: 1.8,
                                background: saveSuccess
                                    ? theme.palette.success.main
                                    : theme.palette.warning.main,
                                "&:hover": {
                                    background: saveSuccess
                                        ? theme.palette.success.dark
                                        : theme.palette.warning.dark,
                                },
                            }}
                        >
                            {savingSkills ? (
                                <Box
                                    sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        border: `2px solid ${alpha("#fff", 0.3)}`,
                                        borderTopColor: "#fff",
                                        animation: "spin 0.8s linear infinite",
                                        "@keyframes spin": {
                                            "100%": { transform: "rotate(360deg)" },
                                        },
                                    }}
                                />
                            ) : saveSuccess ? (
                                <>
                                    <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                    Saved!
                                </>
                            ) : (
                                "Save Config"
                            )}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default SkillsModal;